// Story 10.4 — Toolbar de período. Tabs + custom range (input date).

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { type PeriodPreset, presetToRange, rangeToSearch } from '@/lib/period-presets';
import { cn } from '@/lib/utils';

interface Props {
  preset: PeriodPreset;
  customSince?: string;
  customUntil?: string;
  onChange: (next: { since?: string; until?: string }) => void;
}

const PRESETS: { value: Exclude<PeriodPreset, 'custom'>; label: string }[] = [
  { value: 'today', label: 'Hoy' },
  { value: '7d', label: '7 días' },
  { value: '30d', label: '30 días' },
];

export function AnalyticsToolbar({
  preset,
  customSince,
  customUntil,
  onChange,
}: Props): React.ReactElement {
  function handlePreset(p: Exclude<PeriodPreset, 'custom'>): void {
    onChange(rangeToSearch(p, presetToRange(p)));
  }

  function setCustomBoundary(field: 'since' | 'until', value: string): void {
    if (!value) {
      onChange({ ...{ since: customSince, until: customUntil }, [field]: undefined });
      return;
    }
    // IG-43: `<input type="date">` retorna `YYYY-MM-DD`. Si lo pasamos
    // directo a `new Date(value)`, JS lo interpreta como **UTC midnight**
    // — un user en UTC-6 pidiendo "2026-04-30" pierde 6 horas de "30 abr"
    // local. Construimos la fecha con componentes locales para que
    // `toISOString()` refleje el inicio del día en su zona.
    const [yearRaw, monthRaw, dayRaw] = value.split('-');
    const y = Number(yearRaw);
    const m = Number(monthRaw);
    const d = Number(dayRaw);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return;
    // Para `until` queremos el final del día local (23:59:59.999) para que
    // el rango sea cerrado inclusivo del día seleccionado.
    const local =
      field === 'until'
        ? new Date(y, m - 1, d, 23, 59, 59, 999)
        : new Date(y, m - 1, d, 0, 0, 0, 0);
    const iso = local.toISOString();
    onChange({
      since: field === 'since' ? iso : customSince,
      until: field === 'until' ? iso : customUntil,
    });
  }

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-3 py-3">
        <div className="flex gap-1" role="tablist" aria-label="Preset de período">
          {PRESETS.map((p) => {
            const active = preset === p.value;
            return (
              <button
                key={p.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => handlePreset(p.value)}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-sm transition',
                  active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border hover:bg-muted',
                )}
              >
                {p.label}
              </button>
            );
          })}
          <button
            type="button"
            role="tab"
            aria-selected={preset === 'custom'}
            onClick={() => onChange({ since: customSince, until: customUntil })}
            className={cn(
              'rounded-md border px-3 py-1.5 text-sm transition',
              preset === 'custom'
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border hover:bg-muted',
            )}
          >
            Personalizado
          </button>
        </div>
        {preset === 'custom' && (
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label htmlFor="analytics-since" className="text-xs">
                Desde
              </Label>
              <Input
                id="analytics-since"
                type="date"
                value={customSince ? customSince.slice(0, 10) : ''}
                onChange={(e) => setCustomBoundary('since', e.target.value)}
                className="h-9"
              />
            </div>
            <div>
              <Label htmlFor="analytics-until" className="text-xs">
                Hasta
              </Label>
              <Input
                id="analytics-until"
                type="date"
                value={customUntil ? customUntil.slice(0, 10) : ''}
                onChange={(e) => setCustomBoundary('until', e.target.value)}
                className="h-9"
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
